/**
 * How big things are in this world, in tiles.
 *
 * One place for the measurements the game is actually built around, because
 * they are not only rendering numbers: how steep a slope a character can walk up
 * decides which ground is walkable, which decides where a path has to go, which
 * decides what the landscape generator is allowed to build. Written down here
 * once, they can be read by the thing that shapes the ground, the thing that
 * decides where somebody may stand, and the thing that draws it, and all three
 * agree.
 *
 * The unit is the tile. A tile is one pace across, heights are in the same unit,
 * and everything below follows from a character being a little under two tiles
 * tall.
 */

/** How tall a person is. Everything else is measured against this. */
export const CHARACTER_HEIGHT = 1.75;

/** One storey of a building, which is a person with room over their head. */
export const STOREY_HEIGHT = 2.4;

/**
 * The steepest ground somebody will walk up, as a rise per tile travelled.
 *
 * Two thirds of a tile up for one across is a little over thirty three degrees:
 * steep enough that a hillside is work to climb and shallow enough that it can
 * be climbed. Anything above it is a cliff, and a cliff is not walked up, it is
 * walked round. Diagonals are measured against the longer distance they cover,
 * so a corner is not secretly steeper than the two edges beside it.
 */
export const MAX_CLIMB = 0.66;

/**
 * The greatest sudden lift onto something, as a rise between two tile centres.
 *
 * A quarter of a tile is a kerb, a doorstep, the lip of a path. Anything higher
 * is a ledge, and a ledge is climbed by going round to where it meets the ground
 * rather than by walking into the side of it. This is what stops somebody
 * stepping straight from a beach onto the deck of a dock a person's height above
 * them.
 */
export const MAX_STEP_UP = 0.25;

/**
 * The tallest face a cliff is drawn with, as a rise between two corners.
 *
 * Not a limit on how tall a cliff is. It is a limit on how much of one is drawn
 * in a single step, and it exists because of how the ground is drawn: a surface
 * through corner heights, one corner to a tile. A step of nine tiles between
 * two neighbouring corners is a triangle nine tiles tall and one tile wide, and
 * a row of those is not a cliff, it is a set of stretched slivers meeting at
 * points. From below they read as a fault in the world.
 *
 * Two tiles is about the height of a person and a half: steep enough that
 * nothing climbs it, short enough that each face is a face. A tall cliff is
 * then a stack of them, which is what a cliff looks like in a world built on
 * tiles, and the mountain keeps its height because the material taken off the
 * top is put at the bottom.
 */
export const CLIFF_STEP = 2.2;

/**
 * The steepest ground the generator will build a path across.
 *
 * Under the limit above, with room to spare: a trail worked out on the exact
 * limit becomes unwalkable the moment anything nudges a corner, and everything
 * nudges corners. A path that climbs faster than this switches back instead.
 */
export const PATH_CLIMB = 0.4;

/**
 * How wide ground has to be to count as somewhere to walk.
 *
 * Three tiles: two people passing, or one person and whatever they are walking
 * round. A trail cut narrower than this reads as a crack in the rock rather than
 * as a way through, and pathfinding through a one tile gap looks like a mistake
 * even when it is not.
 */
export const PATH_TILES = 3;

/**
 * The room a building wants, in tiles.
 *
 * Not a rule, a unit: it is what "flat enough to build on" means when the
 * generator is looking for somewhere to put a village. A hut is one of these, a
 * hall is several.
 */
export const BUILDING_TILES = 6;

/**
 * How far a person can be from something and still reach it.
 *
 * One tile: everything in this world is walked to before it is used, so what
 * matters is standing next to it rather than near it.
 */
export const REACH_TILES = 1;

/**
 * How steep one tile is, from its own four corners.
 *
 * North west, north east, south west, south east, the order corners travel
 * everywhere else.
 *
 * How far the tile leans, plus how much it is twisted. The lean is the slope of
 * the flat surface that fits the four corners best, measured the way a slope is
 * measured: rise over the distance actually travelled, whichever way the ground
 * happens to face.
 *
 * This used to be the span between the highest corner and the lowest, which is
 * not a slope. On ground that falls straight north or straight east the two
 * agree, but on ground that falls to the north east the span counts the rise
 * across the tile's diagonal against the width of its side, and reports a
 * hillside as half again as steep as it is. Everything cut round a curve is
 * partly diagonal, so the flanks of the ramp up to Ardor's house came out as
 * cliff while the middle of it walked, and the path was passable down its
 * centre only.
 *
 * The twist is what the span was really there to catch: three corners level and
 * the fourth a long way up is a cliff edge rather than a slope, and no flat
 * surface fits it. Counted on top of the lean, so such a tile is still refused,
 * and refused a little sooner than the span refused it.
 */
export function steepnessOf(corners: readonly number[]): number {
  const [northWest = 0, northEast = 0, southWest = 0, southEast = 0] = corners;
  if (corners.length < 4) {
    let lowest = Infinity;
    let highest = -Infinity;
    for (const held of corners) {
      if (held < lowest) lowest = held;
      if (held > highest) highest = held;
    }
    return Number.isFinite(lowest) ? highest - lowest : 0;
  }

  const alongX = (northEast + southEast - northWest - southWest) / 2;
  const alongZ = (southWest + southEast - northWest - northEast) / 2;
  const twist = Math.abs(northWest + southEast - northEast - southWest) / 2;

  return Math.hypot(alongX, alongZ) + twist;
}

/**
 * Whether a tile is too steep to stand on.
 *
 * This is the rule that decides which ground is walkable at all, and it is
 * applied when the world is made and again whenever anything moves a corner.
 * With it in place, walking between two walkable tiles is always allowed: both
 * of them are gentle, so the ground between them is too. What is left to check
 * is only the things that are not ground.
 */
export function tooSteep(corners: readonly number[]): boolean {
  return steepnessOf(corners) > MAX_CLIMB;
}

/**
 * Whether stepping from one surface up onto another is a step or a climb.
 *
 * For everything that is not ground: a dock, a bridge, a floor upstairs. Those
 * have a height of their own laid over whatever is underneath, so the surface
 * jumps at the edge instead of sloping, and the jump is the whole question.
 * Somebody standing on sand with a deck at their shoulder should walk round to
 * where the deck meets the land, not up the side of it.
 */
export function tooHighAStep(from: number, to: number): boolean {
  return to - from > MAX_STEP_UP;
}
